/**
 * BugReportButton — iter648
 *
 * Малък плаващ бутон „Доклад за проблем" в долен ляв ъгъл (стак заедно с
 * Coach AI navigator, но малко по-горе). При клик отваря dialog с form за
 * описание на проблема + автоматично уловен page URL.
 *
 * Скрит за неаутентикирани потребители (запазваме UI чист на marketing
 * страниците). За auth state използваме `localStorage.access_token`.
 */
import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useLocation } from "react-router-dom";
import { Bug, X, Send, CheckCircle2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STORAGE_KEY = "nutramilo_language";
// iter1090 — Match the actual token key used by `App.js` (was `access_token`,
// which was a legacy name → the button never displayed for authed users).
const TOKEN_STORAGE_KEY = "nutramilo_access_token";

const TX = {
  bg: {
    label: "Докладвайте проблем",
    title: "Докладване на проблем",
    desc: "Опишете какво не работи. Автоматично прилагаме имейла Ви и страницата, на която сте.",
    page: "Текуща страница",
    component: "За кой екран се отнася? (по желание)",
    component_ph: "напр. симулатор на чинията, вход, табло…",
    description: "Какво се случва?",
    description_ph: "Опишете какво очаквахте да се случи и какво се случи всъщност…",
    severity: "Сериозност",
    sev_low: "Дребен проблем",
    sev_normal: "Нормален",
    sev_high: "Висок",
    sev_blocker: "Не мога да продължа",
    submit: "Изпрати",
    cancel: "Отказ",
    success: "Благодарим! Получихме съобщението Ви.",
    error: "Възникна грешка. Моля, опитайте отново след малко.",
  },
  en: {
    label: "Report an issue",
    title: "Report a broken component",
    desc: "Tell us what isn't working. We'll capture your email + current page automatically.",
    page: "Current page",
    component: "Which component? (optional)",
    component_ph: "e.g. Plate Simulator, Login, Dashboard widget…",
    description: "What happens?",
    description_ph: "Describe what you expected vs what actually happened…",
    severity: "Severity",
    sev_low: "Minor",
    sev_normal: "Normal",
    sev_high: "High",
    sev_blocker: "Can't use the app",
    submit: "Send",
    cancel: "Cancel",
    success: "Thanks! Your report was received.",
    error: "Error. Please try again shortly.",
  },
  es: {
    label: "Reportar un problema",
    title: "Reportar un componente roto",
    desc: "Dinos qué no funciona. Capturaremos tu email + la página actual automáticamente.",
    page: "Página actual",
    component: "¿Qué componente? (opcional)",
    component_ph: "ej. Simulador de Plato, Login, widget del Dashboard…",
    description: "¿Qué ocurre?",
    description_ph: "Describe qué esperabas y qué pasó en realidad…",
    severity: "Severidad",
    sev_low: "Menor",
    sev_normal: "Normal",
    sev_high: "Alta",
    sev_blocker: "No puedo usar la app",
    submit: "Enviar",
    cancel: "Cancelar",
    success: "¡Gracias! Tu reporte fue recibido.",
    error: "Error. Inténtalo de nuevo pronto.",
  },
};

export default function BugReportButton() {
  const [open, setOpen] = useState(false);
  const [component, setComponent] = useState("");
  const [description, setDescription] = useState("");
  const [severity, setSeverity] = useState("normal");
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);
  const [authed, setAuthed] = useState(false);
  const location = useLocation();

  const language =
    (typeof window !== "undefined" && localStorage.getItem(STORAGE_KEY)) || "bg";
  const t = TX[language] || TX.bg;

  useEffect(() => {
    const getToken = () => sessionStorage.getItem(TOKEN_STORAGE_KEY) || localStorage.getItem(TOKEN_STORAGE_KEY);
    setAuthed(!!getToken());
    const onStorage = () => setAuthed(!!getToken());
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, [location.pathname]);

  const submit = useCallback(async () => {
    if (!description || description.trim().length < 5) return;
    setSending(true);
    try {
      const token = sessionStorage.getItem(TOKEN_STORAGE_KEY) || localStorage.getItem(TOKEN_STORAGE_KEY);
      await axios.post(
        `${API}/bug-reports`,
        {
          component: component.trim() || null,
          page_url: window.location.pathname + window.location.search,
          description: description.trim(),
          severity,
          user_agent: navigator.userAgent.slice(0, 500),
        },
        { headers: token ? { Authorization: `Bearer ${token}` } : {} }
      );
      setDone(true);
      setTimeout(() => {
        setOpen(false);
        setDone(false);
        setComponent("");
        setDescription("");
        setSeverity("normal");
      }, 2000);
    } catch (e) {
      // eslint-disable-next-line no-alert
      window.alert(t.error + "\n" + (e?.response?.data?.detail || e.message));
    } finally {
      setSending(false);
    }
  }, [component, description, severity, t]);

  // Hide for non-authenticated users on marketing pages — they can use Tawk.to instead.
  if (!authed) return null;

  // Показва се само в потребителския профил — за да не разсейва никъде другаде.
  const path = location.pathname || "/";
  const isProfile = path === "/dashboard/profile" || path.startsWith("/dashboard/profile/");
  if (!isProfile) return null;

  return (
    <>
      {/* Floating button — stacked above Coach AI (also bottom-left) */}
      <button
        type="button"
        onClick={() => setOpen(true)}
        data-testid="bug-report-fab"
        title={t.label}
        className="fixed z-[9996] flex items-center gap-1.5 px-3 py-2 rounded-full text-xs font-bold shadow-md hover:shadow-lg transition-shadow
          bottom-[calc(9rem+env(safe-area-inset-bottom,0px))] left-4
          sm:bottom-[calc(5rem+env(safe-area-inset-bottom,0px))] sm:left-6
          bg-amber-500 text-amber-950 hover:bg-amber-400"
      >
        <Bug className="w-3.5 h-3.5" />
        <span className="hidden sm:inline">{t.label}</span>
      </button>

      {/* Modal */}
      {open && (
        <div
          data-testid="bug-report-modal"
          className="fixed inset-0 z-[10001] bg-black/60 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4"
          onClick={() => !sending && setOpen(false)}
        >
          <div
            className="w-full sm:max-w-md bg-white text-slate-900 rounded-t-2xl sm:rounded-2xl shadow-2xl flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between p-5 border-b">
              <div>
                <h3 className="text-lg font-bold flex items-center gap-2">
                  <Bug className="w-5 h-5 text-amber-600" />
                  {t.title}
                </h3>
                <p className="text-xs text-slate-500 mt-1">{t.desc}</p>
              </div>
              <button onClick={() => !sending && setOpen(false)} className="p-1 hover:bg-slate-100 rounded">
                <X className="w-5 h-5" />
              </button>
            </div>

            {done ? (
              <div className="p-8 text-center" data-testid="bug-report-success">
                <CheckCircle2 className="w-12 h-12 text-emerald-500 mx-auto mb-3" />
                <p className="font-bold text-base">{t.success}</p>
              </div>
            ) : (
              <div className="p-5 space-y-4">
                <div>
                  <label className="text-xs font-bold uppercase tracking-wider text-slate-600">{t.page}</label>
                  <div className="mt-1 text-xs font-mono bg-slate-50 px-3 py-2 rounded break-all">
                    {typeof window !== "undefined" ? window.location.pathname : ""}
                  </div>
                </div>

                <div>
                  <label className="text-xs font-bold uppercase tracking-wider text-slate-600">{t.component}</label>
                  <input
                    type="text"
                    value={component}
                    onChange={(e) => setComponent(e.target.value)}
                    placeholder={t.component_ph}
                    data-testid="bug-report-component"
                    maxLength={120}
                    className="mt-1 w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-400"
                  />
                </div>

                <div>
                  <label className="text-xs font-bold uppercase tracking-wider text-slate-600">{t.description}</label>
                  <textarea
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    placeholder={t.description_ph}
                    data-testid="bug-report-description"
                    rows={4}
                    maxLength={2000}
                    className="mt-1 w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-400 resize-none"
                  />
                  <div className="text-[10px] text-slate-400 text-right mt-1">{description.length}/2000</div>
                </div>

                <div>
                  <label className="text-xs font-bold uppercase tracking-wider text-slate-600">{t.severity}</label>
                  <div className="mt-1 grid grid-cols-2 gap-2">
                    {[
                      { k: "low", label: t.sev_low, c: "bg-slate-100 text-slate-700" },
                      { k: "normal", label: t.sev_normal, c: "bg-amber-100 text-amber-800" },
                      { k: "high", label: t.sev_high, c: "bg-orange-100 text-orange-800" },
                      { k: "blocker", label: t.sev_blocker, c: "bg-rose-100 text-rose-800" },
                    ].map((s) => (
                      <button
                        key={s.k}
                        type="button"
                        onClick={() => setSeverity(s.k)}
                        data-testid={`bug-report-sev-${s.k}`}
                        className={`px-3 py-2 rounded-lg text-xs font-bold border transition-colors ${
                          severity === s.k
                            ? "border-amber-500 ring-2 ring-amber-400 " + s.c
                            : "border-slate-200 hover:bg-slate-50 text-slate-600"
                        }`}
                      >
                        {s.label}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {!done && (
              <div className="border-t p-4 flex items-center justify-end gap-2">
                <button
                  onClick={() => setOpen(false)}
                  disabled={sending}
                  className="px-4 py-2 text-sm font-semibold text-slate-600 hover:bg-slate-50 rounded-lg"
                >
                  {t.cancel}
                </button>
                <button
                  onClick={submit}
                  disabled={sending || description.trim().length < 5}
                  data-testid="bug-report-submit"
                  className="inline-flex items-center gap-1.5 px-5 py-2 text-sm font-bold text-white bg-amber-600 hover:bg-amber-700 rounded-lg disabled:opacity-50"
                >
                  <Send className="w-4 h-4" />
                  {t.submit}
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}
